import { Injectable, Injector } from "@angular/core";
import { environment } from "../../../environments/environment";
import { PlatformService } from "./device/platform.service";
import { Camera, CameraOptions } from '@ionic-native/camera/ngx';
import { ImageService } from "./image.service";
import { File, FileEntry } from '@ionic-native/file/ngx';
import { ModalService } from './modal.service';

import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

declare var cordova: any;

interface CameraInterruptionInfo {
    ageMillis: number;
    durationMillis: number;
    sameProcess: boolean;
    alwaysFinishActivities: boolean;
    protectionExpired: boolean;
    exitReason: string;
}

@Injectable({
  providedIn: 'root'
})
export class PictureService {

    constructor(
        private injector: Injector,
        private imageService: ImageService,
        private platformService: PlatformService,
        private file: File,
        private modalService: ModalService,
        private alertController: AlertController,
        private translate: TranslateService
    ) {
    }

    public async attachPicture(onSelected: (blob: Blob) => Promise<void>): Promise<void> {
        const select = async (fromGallery: boolean) => {
            if (!this.isCameraEnabled()) {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = async () => {
                    const file = input.files?.[0];
                    if (file) {
                        try {
                            await onSelected(file);
                        } catch (error) {
                            await (await this.modalService.createErrorAlertWithDetail(error)).present();
                        }
                    }
                };
                input.click();
                return;
            }
            try {
                const blob = fromGallery ? await this.choosePicture() : await this.takePicture();
                if (blob != null) { await onSelected(blob); }
            } catch (error) {
                await (await this.modalService.createErrorAlertWithDetail(error)).present();
            }
        };
        const alert = await this.alertController.create({
            header: await this.translate.get('FORMS.ATTACHMENT').toPromise(),
            buttons: [
                { text: await this.translate.get('FORMS.CHOOSEPICTURE').toPromise(), handler: () => select(true) },
                { text: await this.translate.get('FORMS.TAKEPICTURE').toPromise(), handler: () => select(false) },
                { text: await this.translate.get('CANCEL').toPromise() }
            ]
        });
        await alert.present();
        await alert.onDidDismiss();
    }

    public isCameraEnabled() {
        return this.platformService.isNative();
    }

    public async choosePicture(): Promise<Blob> {
        if(!this.isCameraEnabled())
            throw new Error('no camera device in browser');

        return await this.getPictureFromCamera(true);
    }

    public async takePicture(): Promise<Blob> {
        if(!this.isCameraEnabled())
            throw new Error('no camera device in browser');

        return await this.getPictureFromCamera();
    }

    private async getPictureFromCamera(fromGallery: boolean = false): Promise<Blob> {
        const isAndroid = this.platformService.isAndroid();

        if (isAndroid) {
            await this.setCameraBackgroundService(
                'start',
                [fromGallery ? 'gallery' : 'camera']
            );
        }

        try {
            return await new Promise<Blob>((resolve, reject) => {

                const camera = this.injector.get(Camera);

                const options: CameraOptions = {
                    encodingType: camera.EncodingType.JPEG,
                    mediaType: camera.MediaType.PICTURE,
                    correctOrientation: true,
                    quality: 75
                };

                camera.getPicture({
                    ...options,
                    targetWidth: isAndroid ? environment.image.formImageResizeMaxSize : undefined,
                    targetHeight: isAndroid ? environment.image.formImageResizeMaxSize : undefined,
                    destinationType: isAndroid ? camera.DestinationType.FILE_URI : camera.DestinationType.DATA_URL,
                    sourceType: fromGallery ? camera.PictureSourceType.PHOTOLIBRARY : camera.PictureSourceType.CAMERA
                }).then((image) => {

                    setTimeout(() => {
                        if (isAndroid) {
                            this.readUriAsBlob(image)
                                .then(resolve)
                                .catch(reject);
                        } else {
                            this.imageService.ConvertDataURLToBlob(this.toDataUrl(image))
                                .then(resolve)
                                .catch(reject);
                        }
                    }, 1000);

                }, reject);
            });
        } finally {
            if (isAndroid) {
                await this.setCameraBackgroundService('stop');
            }
        }
    }

    public async showCameraInterruptionMessage(): Promise<void> {
        if (!this.platformService.isAndroid()) {
            return;
        }

        try {
            const info = await new Promise<CameraInterruptionInfo>((resolve, reject) => {
                cordova.exec(resolve, reject, 'CameraBackgroundService', 'getInterruptionInfo', []);
            });

            if (!info || info.ageMillis > 2 * 60 * 60 * 1000
                || info.exitReason === 'user_requested'
                || info.exitReason === 'package_updated') {
                return;
            }

            let messageKey = 'CAMERA.INTERRUPTED';
            if (info.alwaysFinishActivities && info.sameProcess) {
                messageKey = 'CAMERA.INTERRUPTED_DONT_KEEP_ACTIVITIES';
            } else if (info.exitReason === 'crash') {
                messageKey = 'CAMERA.INTERRUPTED_CRASH';
            } else if (info.exitReason === 'low_memory' || info.exitReason === 'system_kill') {
                messageKey = 'CAMERA.INTERRUPTED_LOW_MEMORY';
            } else if (info.protectionExpired) {
                messageKey = 'CAMERA.INTERRUPTED_TIMEOUT';
            }

            const alert = await this.modalService.createConfirmationAlert(
                'CAMERA.INTERRUPTED_TITLE',
                messageKey,
                { duration: this.formatDuration(info.durationMillis) }
            );
            await alert.present();
            await alert.onDidDismiss();
        } catch (error) {
            console.error('could not check interrupted camera request', error);
        }
    }

    private async setCameraBackgroundService(action: 'start' | 'stop', args: any[] = []): Promise<void> {
        try {
            await new Promise<void>((resolve, reject) => {
                cordova.exec(resolve, reject, 'CameraBackgroundService', action, args);
            });
        } catch (error) {
            console.error(`could not ${action} camera background service`, error);
        }
    }

    private formatDuration(durationMillis: number): string {
        const totalSeconds = Math.max(0, Math.round(durationMillis / 1000));
        if (totalSeconds < 60) {
            return `${totalSeconds} s`;
        }

        const minutes = Math.floor(totalSeconds / 60);
        const seconds = `${totalSeconds % 60}`.padStart(2, '0');
        return `${minutes}:${seconds} min`;
    }

    private toDataUrl(image: string): string {
        if (image.startsWith('data:')) {
            return image;
        }

        return 'data:image/jpeg;base64,' + image;
    }

    private readUriAsBlob(uri: string): Promise<Blob> {
        return this.file.resolveLocalFilesystemUrl(uri)
            .then(entry => new Promise<Blob>((resolve, reject) => {
                (entry as unknown as FileEntry).file(file => resolve(file), reject);
            }));
    }
}
